import { Card, CardActions, CardContent, Grid, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { useCallback } from 'react';
import * as yup from 'yup';
import PmiTextField from 'shell/components/shared/text-field';
import { DateTimePicker } from '@mui/lab';
import { DateTime } from 'luxon';

export interface NonRecoverableErrorForm {
  id?: string;
  operatorCode?: string;
  microService?: string;
  httpMethod?: string;
  api?: string;
  dateTimePublication: DateTime | null;
  eventData?: string;
  eventDescription?: string;
}

const validationSchema = yup.object().shape({});

interface FormErrorEventProps {
  onOk: (form: NonRecoverableErrorForm) => void;
  onCancel: () => void;
  onDelete: () => void;
  title: string;
  disabled?: boolean;
  initialValues?: NonRecoverableErrorForm;
  isEdit?: boolean;
}

const FormNonRecoverableError = ({
  onOk,
  onCancel,
  onDelete,
  title,
  disabled = true,
  initialValues,
  isEdit = false,
}: FormErrorEventProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const formNonRecoverableError = useFormik<NonRecoverableErrorForm>({
    initialValues: initialValues || {
      dateTimePublication: null,
      eventData: '',
      eventDescription: '',
      id: '',
      microService: '',
      api: '',
      httpMethod: '',
      operatorCode: '',
    },
    validationSchema: validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={formNonRecoverableError.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2" color="secondary">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <DateTimePicker
                  aria-label="Data e ora pubblicazione"
                  label="Data e ora pubblicazione"
                  disabled={disabled}
                  value={formNonRecoverableError.values.dateTimePublication || null}
                  onChange={formNonRecoverableError.handleChange}
                  renderInput={(params) => (
                    <PmiTextField
                      {...params}
                      id="dateTimePublication"
                      name="dateTimePublication"
                      fullWidth
                      error={
                        formNonRecoverableError.touched.dateTimePublication &&
                        Boolean(formNonRecoverableError.errors.dateTimePublication)
                      }
                      helperText={formNonRecoverableError.errors.dateTimePublication}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <PmiTextField
                  aria-label="Codice operatore"
                  label="Operatore"
                  id="operatorCode"
                  name="operatorCode"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={formNonRecoverableError.values.operatorCode || ''}
                  disabled={disabled}
                  onChange={formNonRecoverableError.handleChange}
                  error={
                    formNonRecoverableError.touched.operatorCode &&
                    Boolean(formNonRecoverableError.errors.operatorCode)
                  }
                  helperText={formNonRecoverableError.errors.operatorCode}
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <PmiTextField
                  aria-label="Microservizio"
                  label="Microservizio"
                  id="microService"
                  name="microService"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={formNonRecoverableError.values.microService || ''}
                  disabled={disabled}
                  onChange={formNonRecoverableError.handleChange}
                  error={
                    formNonRecoverableError.touched.microService &&
                    Boolean(formNonRecoverableError.errors.microService)
                  }
                  helperText={formNonRecoverableError.errors.microService}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Metodo HTTP"
                  label="httpMethod"
                  id="httpMethod"
                  name="httpMethod"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={formNonRecoverableError.values.httpMethod || ''}
                  disabled={disabled}
                  onChange={formNonRecoverableError.handleChange}
                  error={
                    formNonRecoverableError.touched.httpMethod &&
                    Boolean(formNonRecoverableError.errors.httpMethod)
                  }
                  helperText={formNonRecoverableError.errors.httpMethod}
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <PmiTextField
                  aria-label="Dati in input"
                  label="Dati in input"
                  id="eventData"
                  name="eventData"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={formNonRecoverableError.values.eventData || ''}
                  disabled={disabled || isEdit}
                  onChange={formNonRecoverableError.handleChange}
                  error={
                    formNonRecoverableError.touched.eventData &&
                    Boolean(formNonRecoverableError.errors.eventData)
                  }
                  helperText={formNonRecoverableError.errors.eventDescription}
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <PmiTextField
                  aria-label="Dati in input"
                  label="Dati in input"
                  id="eventData"
                  name="eventData"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={formNonRecoverableError.values.eventData || ''}
                  disabled={disabled || isEdit}
                  onChange={formNonRecoverableError.handleChange}
                  error={
                    formNonRecoverableError.touched.eventData &&
                    Boolean(formNonRecoverableError.errors.eventData)
                  }
                  helperText={formNonRecoverableError.errors.eventDescription}
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <PmiTextField
                  aria-label="Errore"
                  label="Errore"
                  id="eventDescription"
                  name="eventDescription"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={formNonRecoverableError.values.eventDescription || ''}
                  disabled={disabled || isEdit}
                  onChange={formNonRecoverableError.handleChange}
                  error={
                    formNonRecoverableError.touched.eventDescription &&
                    Boolean(formNonRecoverableError.errors.eventDescription)
                  }
                  helperText={formNonRecoverableError.errors.eventDescription}
                />
              </Grid>
            </Grid>
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton variant="contained" color="primary" type="submit">
            Reinoltra
          </PmiButton>
          <PmiButton
            variant="contained"
            color="primary"
            onClick={() => {
              onCancel();
            }}
          >
            Indietro
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onDelete();
            }}
          >
            Cancella
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormNonRecoverableError.displayName = 'Form Non Recoverable Error component';

export default FormNonRecoverableError;
